'use client';

import Link from 'next/link';
import { cn } from 'common/utils';
import ContentLoader from 'react-content-loader';
import { UserTopicTypeWithPostCount } from 'service/user';

// Skeleton loading component for trending items
const TopicSkeletonItem = () => (
  <ContentLoader
    speed={1.5}
    width="100%"
    height={48}
    uniqueKey="trending-item-skeleton"
    viewBox="0 0 300 48"
    backgroundColor="#f3f3f3"
    foregroundColor="#ecebeb"
    className="w-full max-w-full"
    preserveAspectRatio="xMidYMid meet"
  >
    {/* Trending Number */}
    <rect x="0" y="16" rx="2" ry="2" width="12" height="16" />

    {/* Trending Topic */}
    <rect x="32" y="8" rx="4" ry="4" width="120" height="14" />

    {/* Post Count */}
    <rect x="32" y="26" rx="4" ry="4" width="60" height="12" />
  </ContentLoader>
);

const TopicWidget = ({
  topics,
  isFetched,
  className,
  title = 'トレンド',
}: {
  topics: UserTopicTypeWithPostCount[];
  isFetched: boolean;
  className?: string;
  title?: string;
}) => {
  if (isFetched && topics?.length === 0) return null;

  return (
    <div className={cn('all-font-sans pb-2 pt-3', className)}>
      {/* Widget Header */}
      <h3 className="mb-0 text-sm font-semibold leading-none text-pp-text-primary">
        {title}
      </h3>

      {/* Trending List */}
      <div className="mt-4">
        {!isFetched ? (
          // Show skeleton loading when data is not fetched yet
          <>
            {Array.from({ length: 5 }, (_, index) => (
              <div key={index} className="py-2">
                <TopicSkeletonItem />
              </div>
            ))}
          </>
        ) : (
          // Show actual trending data
          topics.map((topic, index) => (
            <div
              key={`${topic.id}-${index}`}
              className="flex items-center gap-3 py-2"
            >
              {/* Trending Number */}
              <div className="w-5 text-sm font-semibold text-pp-text-secondary">
                {index + 1}
              </div>

              <Link
                href={`/p/post-topic/${encodeURIComponent(topic.name)}`}
                className="h-[42px] w-fit cursor-pointer transition-opacity hover:opacity-80"
              >
                <div className="flex items-center gap-1 text-sm font-medium text-pp-text-primary">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#262626"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="size-3"
                  >
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                  </svg>

                  <span>{topic.name}</span>
                </div>
                <div className="text-xs text-pp-text-tertiary">
                  {topic.post_count}投稿
                </div>
              </Link>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TopicWidget;
